import { useAuth } from "@/context/AuthContext";
import { getProfilePictureThumbnail } from "@/utils/getProfilePictureThumbnail";
import Feather from "@expo/vector-icons/Feather";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import { getApp } from "firebase/app";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Animated,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ThemedText } from "./ThemedText";
import { Button } from "./ui/Button";
import CustomModal from "./ui/CustomModal.web";
import { VerifiedDisplayName } from "./ui/VerifiedDisplayName";

const DefaultProfileImage = require("../assets/images/default-profile.webp");

interface SidebarProps {
  isVisible: boolean;
  onClose: () => void;
  highlightSettingsOption?: boolean;
  onHighlightedSettingsPress?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isVisible,
  onClose,
  highlightSettingsOption = false,
  onHighlightedSettingsPress,
}) => {
  const router = useRouter();
  const { logout, user, userData } = useAuth();
  const slideAnim = useRef(new Animated.Value(-250)).current;
  const insets = useSafeAreaInsets();
  const [shouldRender, setShouldRender] = React.useState(isVisible);
  const { t } = useTranslation();
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 600;

  useEffect(() => {
    if (!user?.uid) {
      setUsername(null);
      return;
    }
    Image.prefetch(getProfilePictureThumbnail(user.uid));
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(getFirestore(getApp()), "users", user.uid)
        );
        if (!cancelled && snap.exists()) {
          setUsername((snap.data() as { username?: string }).username ?? null);
        }
      } catch (error) {
        console.error("Sidebar: failed to load username", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: -250,
        duration: 300,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) setShouldRender(false);
      });
    }
  }, [isVisible, slideAnim]);

  const handleLogout = () => {
    setLogoutModalVisible(true);
  };

  const confirmLogout = () => {
    logout();
    setLogoutModalVisible(false);
    onClose();
  };

  const handleProfile = () => {
    router.push({
      pathname: "/public-profile",
      params: { userId: user?.uid },
    });
    onClose();
  };

  const handleSettings = () => {
    if (highlightSettingsOption && onHighlightedSettingsPress) {
      onHighlightedSettingsPress();
      onClose();
      return;
    }

    router.push("/settings");
    onClose();
  };

  const handleHelp = () => {
    // @ts-ignore
    router.push("/help");
    onClose();
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <Pressable
      style={[styles.overlay, { top: insets.top, bottom: insets.bottom }]}
      onPress={onClose}
    >
      <Animated.View
        role="navigation"
        aria-label={t("NAV_ACCOUNT")}
        style={[
          styles.sidebar,
          {
            backgroundColor: "#1e3a8a",
            transform: [{ translateX: slideAnim }],
            paddingTop: insets.top,
            paddingBottom: insets.bottom,
            paddingLeft: insets.left + 20,
            paddingRight: insets.right + 20,
          },
        ]}
      >
        <TouchableOpacity style={styles.option} onPress={handleProfile}>
          <Image
            source={{ uri: getProfilePictureThumbnail(user?.uid) }}
            placeholder={DefaultProfileImage}
            style={styles.avatar}
            accessibilityLabel={t("USER_AVATAR_ALT")}
          />
          <View style={styles.profileTextContainer}>
            <VerifiedDisplayName
              user={userData ?? user}
              style={styles.profileName}
              numberOfLines={1}
              ellipsizeMode="tail"
              fallbackToUsername={false}
              badgeSize={16}
            />
            {username ? (
              <ThemedText
                style={styles.profileHandle}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                @{username}
              </ThemedText>
            ) : null}
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.option,
            highlightSettingsOption && styles.highlightedOption,
          ]}
          onPress={handleSettings}
        >
          <Feather name="settings" size={20} color="#fff" />
          <ThemedText style={styles.optionText}>{t("SETTINGS")}</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity style={styles.option} onPress={handleHelp}>
          <Feather name="help-circle" size={20} color="#fff" />
          <ThemedText style={styles.optionText}>{t("HELP")}</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity style={styles.option} onPress={handleLogout}>
          <Feather name="log-out" size={20} color="#fff" />
          <ThemedText style={styles.optionText}>{t("LOGOUT")}</ThemedText>
        </TouchableOpacity>
      </Animated.View>

      <CustomModal
        visible={logoutModalVisible}
        onClose={() => setLogoutModalVisible(false)}
        title={t("LOGOUT")}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ThemedText>{t("ARE_YOU_SURE_YOU_WANT_TO_LOGOUT")}</ThemedText>
          <div
            style={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: 10,
            }}
          >
            <Button
              text={t("LOGOUT")}
              onPress={confirmLogout}
              style={{
                ...(isSmallScreen ? {} : { flex: 1 }),
                backgroundColor: "red",
              }}
              textStyle={{ textAlign: "center" }}
            />
            <Button
              text={t("CANCEL")}
              onPress={() => setLogoutModalVisible(false)}
              style={{
                ...(isSmallScreen ? {} : { flex: 1 }),
                backgroundColor: "#ccc",
              }}
            />
          </div>
        </div>
      </CustomModal>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10,
  },
  sidebar: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 250,
    height: "100%",
    padding: 20,
    zIndex: 20,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 15,
  },
  highlightedOption: {
    borderWidth: 2,
    borderColor: "#93c5fd",
    borderRadius: 10,
    paddingHorizontal: 10,
    backgroundColor: "rgba(255,255,255,0.12)",
    shadowColor: "#fff",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 10,
  },
  optionText: {
    marginLeft: 10,
    fontSize: 18,
    color: "#fff",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  profileTextContainer: {
    marginLeft: 10,
    flex: 1,
    minWidth: 0,
  },
  profileName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#fff",
    lineHeight: 20,
  },
  profileHandle: {
    fontSize: 13,
    color: "#cbd5f5",
    lineHeight: 16,
  },
});

export default Sidebar;
